@file:OptIn(androidx.compose.ui.ExperimentalComposeUiApi::class)

package org.teddapp.project.ui.mindmap

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.VectorConverter
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.focusable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.detectTransformGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.withTransform
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.geometry.lerp
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isAltPressed
import androidx.compose.ui.input.key.isCtrlPressed
import androidx.compose.ui.input.key.isMetaPressed
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.isCtrlPressed
import androidx.compose.ui.input.pointer.isMetaPressed
import androidx.compose.ui.input.pointer.isShiftPressed
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.TextMeasurer
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.teddapp.project.ui.LocalTeddColors
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import kotlin.math.exp
import kotlin.math.max
import kotlin.math.min
import kotlin.math.roundToInt

// ── Couleurs ──────────────────────────────────────────────────────────────────

/** Les teintes des branches de niveau 1, héritées par leurs descendants. */
internal class MindMapPalette(val root: Color, val branches: List<Color>, val isDark: Boolean) {
    fun branch(index: Int): Color = if (index < 0) root else branches[index % branches.size]

    /** Fond doux d'un nœud de cette teinte. */
    fun soft(color: Color): Color = color.copy(alpha = if (isDark) 0.22f else 0.13f)
}

@Composable
internal fun rememberMindMapPalette(mapColor: String?): MindMapPalette {
    val colors = LocalTeddColors.current
    val fallback = TeddTokens.ModNotes
    return remember(mapColor, colors) {
        val root = mapColor?.let { parseHexColor(it) } ?: fallback
        val light = listOf(0xFF2563EB, 0xFFD97706, 0xFF059669, 0xFFDB2777, 0xFF7C3AED, 0xFF0891B2, 0xFFDC2626, 0xFF65A30D)
        val dark = listOf(0xFF60A5FA, 0xFFFBBF24, 0xFF34D399, 0xFFF472B6, 0xFFA78BFA, 0xFF22D3EE, 0xFFF87171, 0xFFA3E635)
        MindMapPalette(root, (if (colors.isDark) dark else light).map { Color(it) }, colors.isDark)
    }
}

internal fun parseHexColor(hex: String): Color? {
    val h = hex.removePrefix("#")
    if (h.length != 6 && h.length != 8) return null
    val v = h.toLongOrNull(16) ?: return null
    return if (h.length == 6) Color(0xFF000000L or v) else Color(v)
}

// ── Mesure et peinture ────────────────────────────────────────────────────────

/**
 * Mesure les textes et dessine la carte. Un seul objet par écran : il garde les
 * mesures (coûteuses) par nœud et par texte, et les chemins des arêtes par disposition.
 * Partagé entre le canevas à l'écran et l'export en image.
 */
internal class MindMapPainter(
    private val measurer: TextMeasurer,
    private val density: Density,
    body: TextStyle,
    private val palette: MindMapPalette,
    private val ink: Color,
    private val onRoot: Color,
    private val accent: Color,
    private val surface: Color,
    private val fresh: Color,
) {
    private val nodeStyle = body.copy(fontSize = 14.sp, lineHeight = 19.sp)
    private val rootStyle = body.copy(fontSize = 18.sp, lineHeight = 23.sp, fontWeight = FontWeight.SemiBold)
    private val badgeStyle = body.copy(fontSize = 11.sp, lineHeight = 12.sp, fontWeight = FontWeight.SemiBold)

    private val padX = with(density) { 14.dp.toPx() }
    private val padY = with(density) { 8.dp.toPx() }
    private val maxTextWidth = with(density) { 220.dp.toPx() }
    private val radius = with(density) { 10.dp.toPx() }

    private class Measured(val text: String, val root: Boolean, val layout: TextLayoutResult)
    private val texts = HashMap<String, Measured>()
    private val rootIds = HashSet<String>()

    /** Les nœuds racines mesurent plus grand : à déclarer avant la disposition. */
    fun setRoot(id: String?) { rootIds.clear(); if (id != null) rootIds += id }

    fun text(id: String, text: String): TextLayoutResult {
        val root = id in rootIds
        texts[id]?.let { if (it.text == text && it.root == root) return it.layout }
        val shown = text.ifBlank { "…" }
        val layout = measurer.measure(
            shown,
            style = if (root) rootStyle else nodeStyle,
            constraints = Constraints(maxWidth = maxTextWidth.toInt()),
            softWrap = true,
        )
        texts[id] = Measured(text, root, layout)
        return layout
    }

    /** Taille de la boîte d'un nœud, texte mesuré et marges comprises. */
    val measure: (LayoutNode) -> LayoutSize = { n ->
        val t = text(n.id, n.text)
        LayoutSize(t.size.width + 2 * padX, t.size.height + 2 * padY)
    }

    fun forget(ids: Collection<String>) { texts.keys.retainAll(ids.toSet()) }

    // Chemins des arêtes, par couleur, reconstruits seulement quand la disposition change.
    private var edgesFor: MindMapLayoutResult? = null
    private var edgePaths: List<Pair<Color, Path>> = emptyList()
    private var thickPaths: List<Pair<Color, Path>> = emptyList()

    private fun edges(layout: MindMapLayoutResult, rects: (String) -> Rect?): Pair<List<Pair<Color, Path>>, List<Pair<Color, Path>>> {
        if (edgesFor === layout) return edgePaths to thickPaths
        val thin = HashMap<Int, Path>()
        val thick = HashMap<Int, Path>()
        for (e in layout.edges) {
            val p = rects(e.parentId) ?: continue
            val c = rects(e.childId) ?: continue
            val path = (if (e.depth == 1) thick else thin).getOrPut(e.branch) { Path() }
            val (sx, ex) = if (e.side == LayoutSide.Right) p.right to c.left else p.left to c.right
            val sy = p.center.y
            val ey = c.center.y
            val mid = (sx + ex) / 2f
            path.moveTo(sx, sy)
            path.cubicTo(mid, sy, mid, ey, ex, ey)
        }
        edgePaths = thin.map { (b, p) -> palette.branch(b) to p }
        thickPaths = thick.map { (b, p) -> palette.branch(b) to p }
        edgesFor = layout
        return edgePaths to thickPaths
    }

    class Marks(
        val selected: String? = null,
        val editing: String? = null,
        val matches: Set<String> = emptySet(),
        val searching: Boolean = false,
        val fresh: Set<String> = emptySet(),
        val noteCounts: Map<String, Int> = emptyMap(),
    )

    /**
     * Dessine en coordonnées monde. [visible] : la fenêtre monde à dessiner (le reste est
     * sauté) ; [scale] : l'échelle courante, pour le niveau de détail ; [rectOf] : la boîte
     * de chaque nœud, interpolée pendant une animation.
     */
    fun DrawScope.drawMindMap(
        layout: MindMapLayoutResult,
        visible: Rect,
        scale: Float,
        marks: Marks,
        rectOf: (PlacedNode) -> Rect,
        alphaOf: (PlacedNode) -> Float,
    ) {
        if (layout.nodes.isEmpty()) return
        val animated = layout.nodes.any { rectOf(it) != it.rect.toCompose() }
        val (thin, thick) = if (animated) buildEdgesNow(layout, rectOf) else edges(layout) { id -> layout.byId[id]?.rect?.toCompose() }
        val lineW = with(density) { 2.dp.toPx() }
        for ((color, path) in thick) drawPath(path, color.copy(alpha = 0.9f), style = Stroke(lineW * 1.5f))
        if (scale >= LOD_EDGES) for ((color, path) in thin) drawPath(path, color.copy(alpha = 0.75f), style = Stroke(lineW))

        val range = layout.visibleRange(visible.top, visible.bottom)
        val showText = scale >= LOD_TEXT
        val textAlpha = ((scale - LOD_TEXT) / 0.15f).coerceIn(0f, 1f)
        val stroke = with(density) { 1.5.dp.toPx() }
        for (i in range) {
            val n = layout.nodes[i]
            val r = rectOf(n)
            if (r.right < visible.left || r.left > visible.right) continue
            val a = alphaOf(n) * if (marks.searching && n.id !in marks.matches) 0.3f else 1f
            val isRoot = n.depth == 0
            val tint = palette.branch(n.branch)
            val fill = if (isRoot) palette.root else palette.soft(tint)
            drawRoundRect(fill.copy(alpha = fill.alpha * a), r.topLeft, r.size, CornerRadius(radius))
            if (!isRoot) drawRoundRect(tint.copy(alpha = 0.55f * a), r.topLeft, r.size, CornerRadius(radius), style = Stroke(stroke * 0.7f))
            when {
                n.id == marks.selected -> drawRoundRect(accent.copy(alpha = a), r.inflate(stroke * 1.5f).topLeft, r.inflate(stroke * 1.5f).size, CornerRadius(radius + stroke), style = Stroke(stroke * 1.6f))
                n.id in marks.fresh -> drawRoundRect(fresh.copy(alpha = a), r.inflate(stroke).topLeft, r.inflate(stroke).size, CornerRadius(radius + stroke), style = Stroke(stroke * 1.4f))
                marks.searching && n.id in marks.matches -> drawRoundRect(accent.copy(alpha = 0.9f * a), r.inflate(stroke).topLeft, r.inflate(stroke).size, CornerRadius(radius + stroke), style = Stroke(stroke))
            }
            if (showText && n.id != marks.editing) {
                val t = text(n.id, n.node.text)
                val color = (if (isRoot) onRoot else ink).copy(alpha = a * textAlpha)
                drawText(t, color, topLeft = Offset(r.left + padX, r.top + padY))
            }
            // Pastille « +N » sur le bord extérieur d'un nœud replié.
            if (n.hiddenDescendants > 0) {
                val label = measurer.measure("+${n.hiddenDescendants}", badgeStyle)
                val w = label.size.width + padX
                val h = label.size.height + padY * 0.7f
                // Juste à l'extérieur du bord, jamais par-dessus le texte.
                val gap = padY * 0.5f
                val left = if (n.side == LayoutSide.Right || isRoot) r.right + gap else r.left - gap - w
                val top = r.center.y - h / 2f
                drawRoundRect(tint.copy(alpha = a), Offset(left, top), Size(w, h), CornerRadius(h / 2f))
                if (showText) drawText(label, onRoot.copy(alpha = a), Offset(left + padX / 2f, top + padY * 0.35f))
            }
            // Un point avec le nombre de notes, en haut à droite.
            val notes = marks.noteCounts[n.id] ?: 0
            if (notes > 0 && showText) {
                val label = measurer.measure(if (notes > 9) "9+" else notes.toString(), badgeStyle)
                val d = max(label.size.height.toFloat(), label.size.width.toFloat()) + padY * 0.6f
                val c = Offset(r.right - d * 0.35f, r.top + d * 0.1f)
                drawCircle(surface.copy(alpha = a), d / 2f + stroke, c)
                drawCircle(tint.copy(alpha = a), d / 2f, c)
                drawText(label, onRoot.copy(alpha = a), Offset(c.x - label.size.width / 2f, c.y - label.size.height / 2f))
            }
        }
    }

    /** Pendant une animation, les arêtes suivent les boîtes interpolées : pas de cache. */
    private fun buildEdgesNow(layout: MindMapLayoutResult, rectOf: (PlacedNode) -> Rect): Pair<List<Pair<Color, Path>>, List<Pair<Color, Path>>> {
        val saved = edgesFor
        edgesFor = null
        val out = edges(layout) { id -> layout.byId[id]?.let(rectOf) }
        edgesFor = saved
        return out
    }

    companion object {
        /** Sous cette échelle, plus de texte ; sous [LOD_EDGES], plus d'arêtes fines. */
        const val LOD_TEXT = 0.45f
        const val LOD_EDGES = 0.25f
    }
}

internal fun LayoutRect.toCompose(): Rect = Rect(left, top, left + width, top + height)

/** Pixels par unité de molette : 40 par cran sur le bureau ; le navigateur, déjà en pixels, pose 1. */
object MindMapWheel {
    var pixelsPerUnit: Float = 40f
}

// ── Caméra ────────────────────────────────────────────────────────────────────

/**
 * Échelle et décalage de la vue : lus dans le dessin, jamais dans la composition.
 * Les gestes écrivent directement (pas de coroutine par événement de pointeur) ;
 * seuls « Ajuster » et « Centrer » animent.
 */
internal class MindMapCamera {
    var scale by mutableFloatStateOf(1f)
    var offset by mutableStateOf(Offset.Zero)
    var viewport by mutableStateOf(IntSize.Zero)
    /** L'utilisateur a déplacé la vue : on n'ajuste plus tout seul au redimensionnement. */
    var touched = false

    fun toWorld(screen: Offset): Offset = (screen - offset) / scale

    fun zoomAt(pivot: Offset, factor: Float) {
        val target = (scale * factor).coerceIn(MIN_SCALE, MAX_SCALE)
        val k = target / scale
        scale = target
        offset = (offset - pivot) * k + pivot
        touched = true
    }

    fun pan(delta: Offset) {
        offset += delta
        touched = true
    }

    private suspend fun animateOffset(target: Offset) {
        val start = offset
        androidx.compose.animation.core.animate(Offset.VectorConverter, start, target, animationSpec = tween(250)) { value, _ -> offset = value }
    }

    /** Toute la carte dans la fenêtre, avec une marge. */
    suspend fun fit(bounds: Rect, animated: Boolean) {
        if (viewport == IntSize.Zero || bounds.isEmpty) return
        val margin = 48f
        val s = min(viewport.width / (bounds.width + 2 * margin), viewport.height / (bounds.height + 2 * margin)).coerceIn(MIN_SCALE, 1f)
        scale = s
        val target = Offset(viewport.width / 2f - bounds.center.x * s, viewport.height / 2f - bounds.center.y * s)
        if (animated) animateOffset(target) else offset = target
    }

    /** Amène un nœud au centre si une partie en sort ; sinon ne bouge pas. */
    suspend fun ensureVisible(rect: Rect, center: Boolean) {
        if (viewport == IntSize.Zero) return
        val screen = Rect(rect.topLeft * scale + offset, rect.size * scale)
        val inside = screen.left >= 0 && screen.top >= 0 && screen.right <= viewport.width && screen.bottom <= viewport.height
        if (inside && !center) return
        animateOffset(Offset(viewport.width / 2f - rect.center.x * scale, viewport.height / 2f - rect.center.y * scale))
    }

    companion object {
        const val MIN_SCALE = 0.25f
        const val MAX_SCALE = 3f
    }
}

@Composable
internal fun rememberMindMapCamera(): MindMapCamera = remember { MindMapCamera() }

// ── Le canevas ────────────────────────────────────────────────────────────────

/**
 * La carte à l'écran : dessin, déplacement et zoom (doigt, pincement, molette), sélection
 * au toucher, édition au double-toucher, clavier complet. Un seul `Canvas` : les nœuds ne
 * sont pas des composables, sauf celui en édition. La caméra se lit dans le dessin, ce qui
 * redessine sans recomposer.
 */
@Composable
internal fun MindMapCanvas(
    model: MindMapModel,
    camera: MindMapCamera,
    mode: LayoutMode,
    painter: MindMapPainter,
    modifier: Modifier = Modifier,
    inlineEdit: Boolean = true,
    onOpenNotes: (String) -> Unit = {},
    onRequestDelete: (String) -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    val nodes = model.nodes
    val collapsed = model.collapsed
    val engine = remember(painter) { MindMapLayoutEngine(painter.measure) }
    val layout = remember(nodes, collapsed, mode, engine) {
        painter.setRoot(nodes.firstOrNull { it.parent_id == null }?.id)
        engine.layout(model.layoutNodes(), collapsed, mode)
    }
    SideEffect { if (model.layout !== layout) model.layout = layout }
    val current by rememberUpdatedState(layout)

    // Animation entre deux dispositions : les boîtes glissent de l'ancienne à la nouvelle.
    val progress = remember { Animatable(1f) }
    var previous by remember { mutableStateOf<Map<String, Rect>>(emptyMap()) }
    LaunchedEffect(layout) {
        val before = previous
        if (before.isNotEmpty() && layout.nodes.isNotEmpty()) {
            progress.snapTo(0f)
            progress.animateTo(1f, tween(200))
        }
        previous = layout.nodes.associate { it.id to it.rect.toCompose() }
    }

    // Ajuster à l'ouverture et au redimensionnement tant que l'utilisateur n'a pas bougé la vue.
    LaunchedEffect(model.fitRequest, camera.viewport, layout.rootId) {
        if (layout.nodes.isEmpty() || camera.viewport == IntSize.Zero) return@LaunchedEffect
        if (!camera.touched || model.fitRequest > 0) {
            camera.fit(layout.bounds.toCompose(), animated = camera.touched)
            camera.touched = false
        }
    }
    LaunchedEffect(model.centerRequest, layout) {
        val id = model.centerRequest ?: return@LaunchedEffect
        val rect = layout.byId[id]?.rect?.toCompose() ?: return@LaunchedEffect
        camera.ensureVisible(rect, center = false)
        model.centerRequest = null
    }
    LaunchedEffect(model.freshIds) {
        if (model.freshIds.isNotEmpty()) { kotlinx.coroutines.delay(3000); model.freshIds = emptySet() }
    }

    val focus = remember { FocusRequester() }
    val density = LocalDensity.current
    val bg = TeddTokens.Bg

    Box(
        modifier = modifier
            .clipToBounds()
            .background(bg)
            .onSizeChanged { camera.viewport = it }
            .focusRequester(focus)
            .focusable()
            .onKeyEvent { e -> if (model.editingId == null) handleKey(e, model, camera, scope, onOpenNotes, onRequestDelete) else false }
            .pointerInput(Unit) {
                detectTransformGestures(panZoomLock = false) { centroid, pan, zoom, _ ->
                    if (zoom != 1f) camera.zoomAt(centroid, zoom)
                    if (pan != Offset.Zero) camera.pan(pan)
                }
            }
            .pointerInput(Unit) {
                detectTapGestures(
                    onTap = { p ->
                        focus.requestFocus()
                        val hit = current.hit(camera.toWorld(p).x, camera.toWorld(p).y)
                        if (hit == null) model.select(null) else model.select(hit.id)
                    },
                    onDoubleTap = { p ->
                        val hit = current.hit(camera.toWorld(p).x, camera.toWorld(p).y) ?: return@detectTapGestures
                        if (inlineEdit) model.startEdit(hit.id) else { model.select(hit.id); onOpenNotes(hit.id) }
                    },
                    onLongPress = { p ->
                        val hit = current.hit(camera.toWorld(p).x, camera.toWorld(p).y) ?: return@detectTapGestures
                        model.select(hit.id)
                        onOpenNotes(hit.id)
                    },
                )
            }
            // Molette : défilement ; avec Ctrl ou Cmd, zoom autour du curseur. (Boucle
            // d'événements plutôt que onPointerEvent, absent de Compose sur Android.)
            .pointerInput(Unit) {
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent()
                        if (event.type != PointerEventType.Scroll) continue
                        val change = event.changes.firstOrNull() ?: continue
                        val d = change.scrollDelta
                        val mods = event.keyboardModifiers
                        // Le navigateur donne la molette en pixels (100 par cran), le bureau en
                        // crans (1) : on ramène les deux à des pixels, bornés par événement.
                        val k = MindMapWheel.pixelsPerUnit
                        val dx = (d.x * k).coerceIn(-240f, 240f)
                        val dy = (d.y * k).coerceIn(-240f, 240f)
                        if (mods.isCtrlPressed || mods.isMetaPressed) camera.zoomAt(change.position, exp(-dy * 0.003f))
                        else camera.pan(if (mods.isShiftPressed) Offset(-dy, 0f) else Offset(-dx, -dy))
                        change.consume()
                    }
                }
            },
    ) {
        val marks = MindMapPainter.Marks(
            selected = model.selectedId,
            editing = model.editingId,
            matches = model.matches,
            searching = model.searchOpen && model.search.isNotBlank(),
            fresh = model.freshIds,
            noteCounts = model.noteCounts,
        )
        Box(
            Modifier.fillMaxSize().drawBehind {
                val s = camera.scale
                val o = camera.offset
                val t = progress.value
                val before = previous
                withTransform({ translate(o.x, o.y); scale(s, s, Offset.Zero) }) {
                    with(painter) {
                        drawMindMap(
                            layout = current,
                            visible = Rect(-o.x / s, -o.y / s, (size.width - o.x) / s, (size.height - o.y) / s),
                            scale = s,
                            marks = marks,
                            rectOf = { n ->
                                val now = n.rect.toCompose()
                                if (t >= 1f) now else before[n.id]?.let { lerpRect(it, now, t) } ?: now
                            },
                            alphaOf = { n -> if (t >= 1f || n.id in before) 1f else t },
                        )
                    }
                }
            },
        )
        // Le nœud en édition : un vrai champ, posé sur sa boîte et mis à l'échelle de la vue.
        val editingId = model.editingId
        val editing = editingId?.let { layout.byId[it] }
        if (inlineEdit && editing != null) {
            NodeEditor(
                placed = editing,
                camera = camera,
                painter = painter,
                onCommit = { model.commitEdit(editing.id, it); focus.requestFocus() },
                onCancel = { model.editingId = null; focus.requestFocus() },
            )
        }
    }
}

private fun lerpRect(a: Rect, b: Rect, t: Float): Rect = Rect(lerp(a.topLeft, b.topLeft, t), Size(a.width + (b.width - a.width) * t, a.height + (b.height - a.height) * t))

@Composable
private fun NodeEditor(
    placed: PlacedNode,
    camera: MindMapCamera,
    painter: MindMapPainter,
    onCommit: (String) -> Unit,
    onCancel: () -> Unit,
) {
    val density = LocalDensity.current
    var value by remember(placed.id) { mutableStateOf(TextFieldValue(placed.node.text, TextRange(placed.node.text.length))) }
    val requester = remember { FocusRequester() }
    var committed by remember(placed.id) { mutableStateOf(false) }
    val commit = { if (!committed) { committed = true; onCommit(value.text) } }
    LaunchedEffect(placed.id) { requester.requestFocus() }
    val isRoot = placed.depth == 0
    val style = (if (isRoot) TeddType.Title2 else TeddType.Body).copy(
        fontSize = if (isRoot) 18.sp else 14.sp,
        lineHeight = if (isRoot) 23.sp else 19.sp,
        color = if (isRoot) TeddTokens.OnAction else TeddTokens.Ink,
    )
    val r = placed.rect
    val s = camera.scale
    val o = camera.offset
    val padX = 14.dp
    val padY = 8.dp
    BasicTextField(
        value = value,
        onValueChange = { value = it },
        textStyle = style,
        cursorBrush = SolidColor(TeddTokens.Action),
        modifier = Modifier
            .offset { IntOffset(((r.left) * s + o.x).roundToInt(), ((r.top) * s + o.y).roundToInt()) }
            .graphicsLayer { scaleX = s; scaleY = s; transformOrigin = androidx.compose.ui.graphics.TransformOrigin(0f, 0f) }
            .width(with(density) { max(r.width, 120f).toDp() })
            .background(if (isRoot) TeddTokens.Action else TeddTokens.Surface, androidx.compose.foundation.shape.RoundedCornerShape(10.dp))
            .padding(horizontal = padX, vertical = padY)
            .focusRequester(requester)
            .onFocusChanged { if (!it.isFocused && !committed && value.text != placed.node.text) commit() }
            .onPreviewKeyEvent { e ->
                if (e.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                when (e.key) {
                    Key.Enter, Key.NumPadEnter -> if (e.isShiftPressed) false else { commit(); true }
                    Key.Escape -> { committed = true; onCancel(); true }
                    else -> false
                }
            },
    )
}

/** Les raccourcis du canevas, quand aucun champ n'a le clavier. */
private fun handleKey(
    e: androidx.compose.ui.input.key.KeyEvent,
    model: MindMapModel,
    camera: MindMapCamera,
    scope: kotlinx.coroutines.CoroutineScope,
    onOpenNotes: (String) -> Unit,
    onRequestDelete: (String) -> Unit,
): Boolean {
    if (e.type != KeyEventType.KeyDown) return false
    val cmd = e.isCtrlPressed || e.isMetaPressed
    val selected = model.selectedId
    val root = model.root?.id
    when (e.key) {
        Key.DirectionLeft -> { if (e.isAltPressed) model.moveAmongSiblings(-1) else model.selectHorizontal(toRight = false); return true }
        Key.DirectionRight -> { if (e.isAltPressed) model.moveAmongSiblings(1) else model.selectHorizontal(toRight = true); return true }
        Key.DirectionUp -> { if (e.isAltPressed) model.moveAmongSiblings(-1) else if (selected == null) model.select(root) else model.selectSibling(-1); return true }
        Key.DirectionDown -> { if (e.isAltPressed) model.moveAmongSiblings(1) else if (selected == null) model.select(root) else model.selectSibling(1); return true }
        Key.Tab -> { if (e.isShiftPressed) model.addSibling() else model.addChild(); return true }
        Key.Enter, Key.NumPadEnter -> { selected?.let { model.startEdit(it) } ?: root?.let { model.select(it) }; return true }
        Key.Escape -> { if (model.searchOpen) { model.searchOpen = false; model.search = "" } else model.select(null); return true }
        Key.Spacebar -> { model.toggleCollapse(); return true }
        Key.Delete, Key.Backspace -> { selected?.let(onRequestDelete); return true }
        Key.F -> { if (cmd) { model.searchOpen = true } else selected?.let { model.centerRequest = it; model.layout.byId[it]?.let { p -> scope.launch { camera.ensureVisible(p.rect.toCompose(), center = true) } } }; return true }
        Key.N -> { if (!cmd) selected?.let(onOpenNotes); return !cmd }
        Key.Plus, Key.Equals, Key.NumPadAdd -> { if (cmd) { camera.zoomAt(Offset(camera.viewport.width / 2f, camera.viewport.height / 2f), 1.2f); return true } }
        Key.Minus, Key.NumPadSubtract -> { if (cmd) { camera.zoomAt(Offset(camera.viewport.width / 2f, camera.viewport.height / 2f), 1 / 1.2f); return true } }
        Key.Zero, Key.NumPad0 -> { if (cmd) { model.fitRequest++; return true } }
        else -> return false
    }
    return false
}
